/**
 * Glossary Component JS
 * Handles keyword search and letter filter for the glossary page.
 */

class Glossary {
	constructor() {
		this.glossary = document.querySelector( '.glossary' );
		if ( ! this.glossary ) return;

		this.searchInput  = this.glossary.querySelector( '.glossary__search-input' );
		this.letterBtns   = this.glossary.querySelectorAll( '.glossary__letter-btn:not(.glossary__letter-btn--disabled)' );
		this.sections     = this.glossary.querySelectorAll( '.glossary__letter-section' );
		this.noResults    = this.glossary.querySelector( '.glossary__no-results' );
		this.activeFilter = null;
		this.termOriginals = [];

		this.init();
	}

	init() {
		this.setStickyOffset();
		window.addEventListener( 'resize', () => this.setStickyOffset() );

		this.cacheOriginals();

		if ( this.searchInput ) {
			this.searchInput.addEventListener( 'input', () => this.filter() );
		}

		this.letterBtns.forEach( btn => {
			btn.addEventListener( 'click', () => this.handleLetterClick( btn ) );
		} );
	}

	setStickyOffset() {
		const header  = document.querySelector( '.site-header' );
		const jumpNav = document.querySelector( '.jump-nav' );
		const headerH  = header  ? header.getBoundingClientRect().height  : 0;
		const jumpNavH = jumpNav ? jumpNav.getBoundingClientRect().height : 0;
		document.documentElement.style.setProperty( '--glossary-controls-top', ( headerH + jumpNavH ) + 'px' );
	}

	cacheOriginals() {
		this.glossary.querySelectorAll( '.glossary__term' ).forEach( term => {
			const name = term.querySelector( '.glossary__term-name' );
			const def  = term.querySelector( '.glossary__term-definition' );
			this.termOriginals.push( {
				term,
				name: name ? name.innerHTML : '',
				def:  def  ? def.innerHTML  : '',
			} );
		} );
	}

	highlight( html, query ) {
		const escaped = query.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );
		const regex   = new RegExp( `(${ escaped })`, 'gi' );
		return html.replace( /(<[^>]+>|[^<]+)/g, match => {
			if ( match.startsWith( '<' ) ) return match;
			return match.replace( regex, '<mark class="glossary__highlight">$1</mark>' );
		} );
	}

	handleLetterClick( btn ) {
		const letter = btn.dataset.letter;

		if ( this.activeFilter === letter ) {
			// Toggle off — show all sections
			this.activeFilter = null;
			btn.classList.remove( 'glossary__letter-btn--active' );
			btn.setAttribute( 'aria-pressed', 'false' );
		} else {
			// Deactivate any previously active button
			this.letterBtns.forEach( b => {
				b.classList.remove( 'glossary__letter-btn--active' );
				b.setAttribute( 'aria-pressed', 'false' );
			} );
			this.activeFilter = letter;
			btn.classList.add( 'glossary__letter-btn--active' );
			btn.setAttribute( 'aria-pressed', 'true' );
		}

		this.filter();
	}

	filter() {
		const query        = this.searchInput ? this.searchInput.value.trim().toLowerCase() : '';
		let   totalVisible = 0;

		// Restore all originals before re-applying highlights
		this.termOriginals.forEach( ( { term, name: origName, def: origDef } ) => {
			const name = term.querySelector( '.glossary__term-name' );
			const def  = term.querySelector( '.glossary__term-definition' );
			if ( name ) name.innerHTML = origName;
			if ( def )  def.innerHTML  = origDef;
		} );

		this.sections.forEach( section => {
			const sectionLetter = section.dataset.letter;
			const letterMatch   = ! this.activeFilter || sectionLetter === this.activeFilter;

			if ( ! letterMatch ) {
				section.setAttribute( 'aria-hidden', 'true' );
				return;
			}

			let sectionHasVisible = false;
			const sectionTerms    = section.querySelectorAll( '.glossary__term' );

			sectionTerms.forEach( term => {
				const termData  = ( term.dataset.term || '' ).toLowerCase();
				const termMatch = ! query || termData.includes( query );

				if ( termMatch ) {
					term.removeAttribute( 'aria-hidden' );
					sectionHasVisible = true;
					totalVisible++;

					if ( query ) {
						const name = term.querySelector( '.glossary__term-name' );
						const def  = term.querySelector( '.glossary__term-definition' );
						if ( name ) name.innerHTML = this.highlight( name.innerHTML, query );
						if ( def )  def.innerHTML  = this.highlight( def.innerHTML,  query );
					}
				} else {
					term.setAttribute( 'aria-hidden', 'true' );
				}
			} );

			if ( sectionHasVisible ) {
				section.removeAttribute( 'aria-hidden' );
			} else {
				section.setAttribute( 'aria-hidden', 'true' );
			}
		} );

		if ( this.noResults ) {
			this.noResults.hidden = totalVisible > 0;
		}
	}
}

new Glossary();
